import type { ReactNode } from 'react'

/**
 * El único layout de la app.
 *
 * **Ninguna pantalla define su propio ancho, su padding ni su scroll.** Antes lo
 * hacía cada una por su cuenta y había siete anchos distintos —896, 768, 672,
 * 448, 384 y dos pantallas sin contenedor— así que al cambiar de pestaña el
 * contenido saltaba de lugar. Acá hay un solo número y vale para todas.
 *
 * Dos modos, y la diferencia es de contenido, no de gusto:
 *
 *   normal  Texto y formularios. Ancho acotado y la PÁGINA scrollea. Un
 *           `overflow-auto` dentro de la página crea un segundo scroll que
 *           compite con el del navegador: la rueda mueve el de adentro, la
 *           barra de la derecha no se entera, y el usuario no sabe cuál está
 *           tocando.
 *   pleno   La grilla de la agenda y la ficha con panel lateral. Necesitan
 *           todo el ancho y administran su propio desplazamiento, así que el
 *           shell les da la altura de la ventana y se corre.
 */

/** El único ancho de contenido de la app. Si hay que cambiarlo, se cambia acá. */
const ANCHO_CONTENIDO = 'max-w-[1100px]'

export interface AppShellProps {
  /** La barra vertical de la clínica. */
  nav: ReactNode
  /** El banner del paciente abierto, si hay uno. Va arriba del contenido. */
  banner?: ReactNode
  children: ReactNode
  /** La pantalla ocupa todo el ancho y maneja su propio scroll. */
  pleno?: boolean
}

export function AppShell({ nav, children, banner, pleno = false }: AppShellProps) {
  return (
    <div className="flex h-dvh">
      {nav}

      <div className={`flex min-w-0 flex-1 flex-col ${pleno ? '' : 'superficie-base'}`}>
        {banner}

        {pleno ? (
          // `min-h-0` es lo que permite que el hijo se encoja dentro del flex y
          // scrollee él. Sin eso, crece hasta desbordar la ventana.
          <main className="min-h-0 flex-1">{children}</main>
        ) : (
          <main className="flex-1 overflow-y-auto">
            {/* Alineado a la izquierda y no centrado: con la barra vertical
              ocupando 208 px, centrar el contenedor deja un hueco muerto
              enorme a la izquierda y el contenido se lee descolgado de la
              navegación. El ancho máximo se conserva. */}
          <div className={`w-full ${ANCHO_CONTENIDO} px-4 py-6 md:px-6`}>{children}</div>
          </main>
        )}
      </div>
    </div>
  )
}

/**
 * El encabezado de página. Lo usan todas para que el título arranque siempre a
 * la misma altura y con el mismo tamaño.
 */
export function TituloPagina({
  titulo,
  bajada,
  acciones,
}: {
  titulo: string
  bajada?: string
  acciones?: ReactNode
}) {
  return (
    <header className="mb-6 flex flex-wrap items-start gap-x-4 gap-y-2">
      <div className="min-w-0 flex-1">
        <h1 className="text-2xl font-medium leading-tight text-slate-900">{titulo}</h1>
        {bajada && <p className="mt-1 text-sm text-slate-500">{bajada}</p>}
      </div>
      {acciones && <div className="flex shrink-0 items-center gap-2">{acciones}</div>}
    </header>
  )
}
